import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowUp, ArrowDown, ArrowUpDown, Search, Pencil, RefreshCw, Building2, TrendingUp, TrendingDown, AlertCircle, Star, Sparkles, Database } from "lucide-react";
import BDCEditDialog from "../components/BDCEditDialog";
import WhyTooltip from "../components/WhyTooltip";
import { toast } from "sonner";

// ── Column metadata.  Each col has a stable sort accessor + tooltip help text. ──
const COLUMNS = [
  { key: "ticker", label: "Ticker", align: "left",
    acc: (r) => (r.ticker || "").toUpperCase(),
    help: "Exchange ticker. Click to open the SEC EDGAR filing index." },
  { key: "star", label: "", align: "left", sortable: false,
    acc: () => 0, help: "Watch this BDC to receive cut-risk emails for it." },
  { key: "name", label: "Name", align: "left",
    acc: (r) => (r.name || "").toLowerCase(),
    help: "Full company name (auto-filled by Finnhub)." },
  { key: "current_price", label: "Price", align: "right",
    acc: (r) => r.current_price || 0,
    help: "Live price (Finnhub, ~15-min refresh)." },
  { key: "market_cap_usd", label: "Mkt Cap", align: "right",
    acc: (r) => r.market_cap_usd || 0,
    help: "Market capitalization in USD." },
  { key: "dividend_yield", label: "Div Yld", align: "right",
    acc: (r) => r.dividend_yield || 0,
    help: "Forward dividend yield = annual dividend ÷ price. Live-recomputed." },
  { key: "annual_dividend", label: "Div FWD", align: "right",
    acc: (r) => r.annual_dividend || 0,
    help: "Forward annualized dividend per share." },
  { key: "payment_frequency", label: "Freq", align: "right",
    acc: (r) => r.payment_frequency || 0,
    help: "Payments per year (4 = quarterly, 12 = monthly)." },
  { key: "latest_nav_per_share", label: "NAV", align: "right",
    acc: (r) => r.latest_nav_per_share || r.book_value_per_share || 0,
    help: "Most recent NAV per share. Manual entry preferred; falls back to Finnhub book value." },
  { key: "nav_premium_pct", label: "P/NAV", align: "right",
    acc: (r) => r.nav_premium_pct == null ? -9999 : r.nav_premium_pct,
    help: "Price-to-NAV premium. Positive = trading above NAV." },
  { key: "nii_payout_ratio", label: "Payout", align: "right",
    acc: (r) => r.nii_payout_ratio == null ? 9999 : r.nii_payout_ratio,
    help: "NII Payout Ratio = TTM dividend ÷ TTM NII. <100% means covered." },
  { key: "dividend_coverage", label: "Cover", align: "right",
    acc: (r) => r.dividend_coverage == null ? -1 : r.dividend_coverage,
    help: "Dividend Coverage = TTM NII ÷ TTM dividend. >1.0× means covered." },
  { key: "debt_to_equity", label: "D/E", align: "right",
    acc: (r) => r.debt_to_equity == null ? -1 : r.debt_to_equity,
    help: "Debt to Equity ratio (Finnhub /basic-financials, quarterly preferred)." },
  { key: "non_accrual_pct", label: "Non-Acc", align: "right",
    acc: (r) => r.non_accrual_pct == null ? -1 : r.non_accrual_pct,
    help: "% of portfolio on non-accrual. Manual entry (SEC EDGAR coming soon)." },
  { key: "pik_income_pct", label: "PIK", align: "right",
    acc: (r) => r.pik_income_pct == null ? -1 : r.pik_income_pct,
    help: "% of income from Payment-In-Kind. Higher = lower-quality income." },
  { key: "management_type", label: "Mgmt", align: "left",
    acc: (r) => r.management_type || "z",
    help: "Internal or external management. Manual entry." },
  { key: "actions", label: "", align: "right", sortable: false, acc: () => 0, help: "" },
];

const MGMT_CHIP = {
  internal: "bg-groen/25 text-groen-dark border border-groen/40",
  external: "bg-paars/25 text-paars-dark border border-paars/40",
};

// Sort indicator for the column header (returns null for non-sortable cols).
function getSortIcon(col, sort) {
  if (col.sortable === false) return null;
  if (sort.key !== col.key) return ArrowUpDown;
  return sort.dir === "asc" ? ArrowUp : ArrowDown;
}

// Tailwind color class for the P/NAV cell.
function navPremiumClass(pct) {
  if (pct == null) return "text-graphite/45";
  return pct > 0 ? "text-burgundy" : "text-groen-dark";
}

// Tailwind color class for the dividend-coverage cell.
function coverageClass(cov) {
  if (cov == null) return "text-graphite/45";
  return cov >= 1 ? "text-groen-dark" : "text-burgundy";
}

// Short label for management_type chip ("INT" / "EXT" / uppercase fallback).
function mgmtLabel(t) {
  if (t === "internal") return "INT";
  if (t === "external") return "EXT";
  return (t || "").toUpperCase();
}

function PayoutBadge({ value, coverage }) {
  // Single source of UX for the relationship between payout / coverage.
  if (value == null && coverage == null) return <span className="text-graphite/45">—</span>;
  if (value == null) return <span className="font-mono text-graphite/45">{coverage.toFixed(2)}×</span>;
  const safe = value < 100;
  return (
    <span
      className={`font-mono ${safe ? "text-groen-dark" : "text-burgundy"}`}
      title={`${value}% paid out · coverage ${coverage ? coverage.toFixed(2) + "×" : "n/a"}`}
    >
      {value}%
    </span>
  );
}

export default function DatabaseBDC() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState({ key: "market_cap_usd", dir: "desc" });
  const [editTicker, setEditTicker] = useState(null);
  const [watchedSet, setWatchedSet] = useState(new Set());
  // ── Background-job tracking (for EDGAR + LLM bulk refresh, which take
  // longer than the 60s Kong gateway timeout).
  const [activeJob, setActiveJob] = useState(null); // {job_id, type, total, completed, written, errors, status}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: bdcs }, { data: watch }] = await Promise.all([
        api.get("/database/bdc"),
        api.get("/watchlist").catch(() => ({ data: [] })),
      ]);
      setRows(bdcs);
      setWatchedSet(new Set((watch || []).map((w) => w.ticker)));
    } catch (e) {
      toast.error("Failed to load BDC database");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rehydrate any in-flight job after a tab refresh — checks last 5 jobs
  // for the current admin user and re-attaches if still running.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/jobs", { params: { limit: 5 } });
        const running = (data || []).find(
          (j) => j.status === "running" &&
                 ["edgar_refresh_all", "edgar_llm_extract_all"].includes(j.type),
        );
        if (running) setActiveJob(running);
      } catch (e) { void e; }
    })();
  }, [isAdmin]);

  // Poll the active job every 3s while it's running. Refresh table on completion.
  useEffect(() => {
    if (!activeJob || activeJob.status !== "running") return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/jobs/${activeJob.job_id}`);
        setActiveJob(data);
        if (data.status === "completed") {
          const r = data.result || {};
          const written = r.quarters_written_total ?? r.succeeded ?? 0;
          const friendly = data.type === "edgar_llm_extract_all"
            ? `LLM extract done · ${written} quarters extracted across ${r.tickers_total || data.total} BDCs`
            : `EDGAR refresh done · ${r.succeeded}/${r.total} succeeded · ${data.written} quarters written`;
          toast.success(friendly);
          await load();
        } else if (data.status === "failed") {
          toast.error(`Job failed: ${data.error_message || "unknown error"}`);
        }
      } catch (e) { void e; }
    }, 3000);
    return () => clearInterval(id);
  }, [activeJob, load]);

  const toggleStar = async (ticker) => {
    const wasStarred = watchedSet.has(ticker);
    // Optimistic update
    setWatchedSet((s) => {
      const next = new Set(s);
      if (wasStarred) next.delete(ticker); else next.add(ticker);
      return next;
    });
    try {
      if (wasStarred) {
        await api.delete(`/watchlist/${ticker}`);
        toast.success(`${ticker} removed from watchlist`);
      } else {
        await api.post(`/watchlist/${ticker}`);
        toast.success(`${ticker} added to watchlist · you'll get cut-risk alerts`);
      }
    } catch (e) {
      // Revert on error
      setWatchedSet((s) => {
        const next = new Set(s);
        if (wasStarred) next.add(ticker); else next.delete(ticker);
        return next;
      });
      toast.error("Watchlist update failed");
    }
  };

  const display = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let out = q
      ? rows.filter((r) => (r.ticker || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q))
      : rows;
    if (sort.key) {
      const col = COLUMNS.find((c) => c.key === sort.key);
      if (col && col.sortable !== false) {
        const dir = sort.dir === "desc" ? -1 : 1;
        out = [...out].sort((a, b) => {
          const va = col.acc(a), vb = col.acc(b);
          if (va < vb) return -1 * dir;
          if (va > vb) return 1 * dir;
          return 0;
        });
      }
    }
    return out;
  }, [rows, filter, sort]);

  const toggleSort = (key) => {
    const col = COLUMNS.find((c) => c.key === key);
    if (col?.sortable === false) return;
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "desc" };
      if (prev.dir === "desc") return { key, dir: "asc" };
      return { key: null, dir: null };
    });
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10 flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::DATABASE::BDC</div>
          <h1 className="text-2xl font-bold tracking-tight text-graphite" data-testid="bdc-page-title">Business Development Companies</h1>
          <p className="text-xs text-graphite/55 mt-1 max-w-2xl">
            {rows.length} BDCs in the universe. Price, market cap and book value auto-refresh via Finnhub.
            NII, dividend per share and D/E are pulled from SEC EDGAR XBRL; non-accrual %
            and PIK % are LLM-extracted from each 10-Q&apos;s prose (Claude Haiku) — admin entries always win.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/database/bdc/discover"
            data-testid="bdc-discover-link"
            className="px-3 py-2 border border-paars-dark/40 text-paars-dark text-xs font-semibold tracking-widest uppercase hover:bg-paars/20 flex items-center gap-1.5"
            title="Open the ranked Discover lists"
          >
            <Sparkles className="w-3.5 h-3.5" /> Discover
          </Link>
          {isAdmin && (
            <>
            <button
              data-testid="bdc-edgar-refresh-all"
              disabled={activeJob && activeJob.status === "running"}
              onClick={async () => {
                if (!window.confirm("Pull XBRL fundamentals from SEC EDGAR for every BDC?\nRuns in the background — takes ~3 min for 47 BDCs. You'll see live progress here.")) return;
                try {
                  const { data } = await api.post("/admin/bdc/edgar-refresh-all");
                  setActiveJob({ ...data, type: "edgar_refresh_all", completed: 0, written: 0, errors: 0 });
                  toast.info(`EDGAR refresh started · ${data.total} BDCs queued`);
                } catch (e) { toast.error("Failed to start EDGAR refresh"); }
              }}
              className="px-3 py-2 border border-graphite/30 text-graphite/80 text-xs font-semibold tracking-widest uppercase hover:bg-stone/40 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              title="Pull from SEC EDGAR (admin) — runs in background"
            >
              <Database className="w-3.5 h-3.5" /> EDGAR
            </button>
            <button
              data-testid="bdc-edgar-llm-extract-all"
              disabled={activeJob && activeJob.status === "running"}
              onClick={async () => {
                if (!window.confirm(
                  "LLM-extract Non-Accrual % and PIK % from the last 2 10-Q filings of every BDC?\n" +
                  "Runs in the background — takes ~12 min. Uses ~$0.20 of LLM credits."
                )) return;
                try {
                  const { data } = await api.post("/admin/bdc/edgar-llm-extract-all");
                  setActiveJob({ ...data, type: "edgar_llm_extract_all", completed: 0, written: 0, errors: 0 });
                  toast.info(`LLM extract started · ${data.total} BDCs queued`);
                } catch (e) { toast.error("Failed to start LLM extraction"); }
              }}
              className="px-3 py-2 border border-paars-dark/40 text-paars-dark text-xs font-semibold tracking-widest uppercase hover:bg-paars/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              title="Run LLM extraction across all BDCs (admin) — runs in background"
            >
              <Sparkles className="w-3.5 h-3.5" /> LLM
            </button>
            </>
          )}
          {/* Live progress pill — shows when a background job is running. */}
          {activeJob && activeJob.status === "running" && (
            <div
              data-testid="bg-job-progress"
              className="flex items-center gap-2 px-3 py-2 bg-paars/15 border border-paars-dark/30 text-paars-dark text-xs font-mono"
              title={`Job ${activeJob.job_id}`}
            >
              <span className="inline-block w-2 h-2 bg-paars-dark rounded-full animate-pulse" />
              <span className="font-semibold tracking-wider uppercase">
                {activeJob.type === "edgar_llm_extract_all" ? "LLM" : "EDGAR"}
              </span>
              <span data-testid="bg-job-counter">
                {activeJob.completed}/{activeJob.total}
              </span>
              <span className="text-graphite/55">·</span>
              <span>{activeJob.written} quarters written</span>
              {activeJob.errors > 0 && (
                <>
                  <span className="text-graphite/55">·</span>
                  <span className="text-burgundy">{activeJob.errors} err</span>
                </>
              )}
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-graphite/45" />
            <input
              data-testid="bdc-filter-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter ticker or name…"
              className="pl-9 pr-3 py-2 border border-stone bg-champagne text-sm w-64 focus:outline-none focus:border-paars-dark"
            />
          </div>
          <button
            data-testid="bdc-refresh"
            onClick={load}
            className="p-2 border border-stone hover:bg-stone/40 text-graphite/70 hover:text-graphite"
            title="Reload from server"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone border border-stone">
          <Stat testId="bdc-stat-count" label="Tickers tracked" value={rows.length} />
          <Stat
            testId="bdc-stat-covered"
            label="Dividend covered"
            value={rows.filter((r) => r.dividend_coverage != null && r.dividend_coverage >= 1).length}
            sub="Cov ≥ 1.00×"
            accent="text-groen-dark"
          />
          <Stat
            testId="bdc-stat-undercovered"
            label="Under-earning"
            value={rows.filter((r) => r.dividend_coverage != null && r.dividend_coverage < 1).length}
            sub="Cov < 1.00×"
            accent="text-burgundy"
          />
          <Stat
            testId="bdc-stat-pending"
            label="Awaiting data"
            value={rows.filter((r) => r.nii_payout_ratio == null).length}
            sub="No NII on file"
            accent="text-graphite/55"
          />
        </div>

        {/* Table */}
        <div className="border border-stone bg-champagne overflow-x-auto" data-testid="bdc-table">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-stone/95 backdrop-blur-sm border-b border-stone">
                {COLUMNS.map((col) => {
                  const isSorted = sort.key === col.key;
                  const Icon = getSortIcon(col, sort);
                  return (
                    <th
                      key={col.key}
                      title={col.help}
                      onClick={() => toggleSort(col.key)}
                      data-testid={`bdc-col-${col.key}`}
                      className={`px-3 py-3 text-[10px] tracking-[0.2em] uppercase font-bold ${
                        col.align === "right" ? "text-right" : "text-left"
                      } ${isSorted ? "text-paars-dark" : "text-graphite/60"} ${
                        col.sortable === false ? "" : "cursor-pointer hover:text-paars-dark select-none"
                      }`}
                    >
                      <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""}`}>
                        {col.label}
                        {Icon && <Icon className={`w-3 h-3 ${isSorted ? "opacity-100" : "opacity-30"}`} strokeWidth={2.5} />}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={COLUMNS.length} className="p-6 text-center text-graphite/45 font-mono text-xs">LOADING…</td></tr>
              )}
              {!loading && display.length === 0 && (
                <tr><td colSpan={COLUMNS.length} className="p-6 text-center text-graphite/55 font-mono text-xs">
                  No BDCs match &ldquo;{filter}&rdquo;
                </td></tr>
              )}
              {display.map((r) => {
                const nav = r.latest_nav_per_share || r.book_value_per_share;
                const navIsFallback = !r.latest_nav_per_share && !!r.book_value_per_share;
                return (
                  <tr key={r.ticker} data-testid={`bdc-row-${r.ticker}`} className="border-b border-stone/60 hover:bg-white">
                    <td className="px-3 py-3">
                      <a
                        href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${r.ticker}&type=10-Q&dateb=&owner=include&count=10`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open SEC EDGAR filings"
                        className="font-mono font-semibold text-paars-dark hover:underline"
                      >
                        {r.ticker}
                      </a>
                    </td>
                    <td className="px-3 py-3">
                      <button
                        data-testid={`bdc-star-${r.ticker}`}
                        onClick={() => toggleStar(r.ticker)}
                        aria-pressed={watchedSet.has(r.ticker)}
                        title={watchedSet.has(r.ticker) ? "Unwatch · stop cut-risk emails" : "Watch · get cut-risk emails"}
                        className={`p-1 transition-colors ${watchedSet.has(r.ticker) ? "text-gold-dark" : "text-graphite/30 hover:text-gold-dark"}`}
                      >
                        <Star className="w-4 h-4" fill={watchedSet.has(r.ticker) ? "currentColor" : "none"} />
                      </button>
                    </td>
                    <td className="px-3 py-3 text-graphite/70 max-w-[200px] truncate">{r.name || "—"}</td>
                    <td className="px-3 py-3 font-mono text-right text-graphite">{r.current_price ? fmtMoney(r.current_price) : "—"}</td>
                    <td className="px-3 py-3 font-mono text-right text-graphite/80">{r.market_cap_usd ? `$${(r.market_cap_usd / 1_000_000_000).toFixed(2)}B` : "—"}</td>
                    <td className="px-3 py-3 font-mono text-right text-paars-dark">{r.dividend_yield != null ? fmtPct(r.dividend_yield) : "—"}</td>
                    <td className="px-3 py-3 font-mono text-right text-graphite/80">{r.annual_dividend != null ? fmtMoney(r.annual_dividend) : "—"}</td>
                    <td className="px-3 py-3 font-mono text-right text-graphite/60">{r.payment_frequency || "—"}</td>
                    <td className="px-3 py-3 font-mono text-right">
                      {nav ? (
                        <span className={navIsFallback ? "text-graphite/55" : "text-graphite"}
                              title={navIsFallback ? "Finnhub book value (proxy)" : "Manual NAV entry"}>
                          {fmtMoney(nav)}
                        </span>
                      ) : <span className="text-graphite/45">—</span>}
                    </td>
                    <td className={`px-3 py-3 font-mono text-right ${navPremiumClass(r.nav_premium_pct)}`}>
                      {r.nav_premium_pct != null ? `${r.nav_premium_pct > 0 ? "+" : ""}${r.nav_premium_pct.toFixed(1)}%` : "—"}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <PayoutBadge value={r.nii_payout_ratio} coverage={r.dividend_coverage} />
                    </td>
                    <td className={`px-3 py-3 font-mono text-right ${coverageClass(r.dividend_coverage)}`}>
                      {r.dividend_coverage != null ? `${r.dividend_coverage.toFixed(2)}×` : "—"}
                    </td>
                    <td className="px-3 py-3 font-mono text-right text-graphite/80">
                      {r.debt_to_equity != null ? r.debt_to_equity.toFixed(2) : "—"}
                    </td>
                    <td className={`px-3 py-3 font-mono text-right ${r.non_accrual_pct != null && r.non_accrual_pct > 2 ? "text-burgundy" : "text-graphite/70"}`}>
                      <span className="inline-flex items-center justify-end">
                        {r.non_accrual_pct != null ? `${r.non_accrual_pct.toFixed(2)}%` : "—"}
                        <WhyTooltip
                          excerpt={r.llm_excerpts?.non_accrual_pct}
                          model={r.llm_model}
                          confidence={r.llm_confidence}
                          extractedAt={r.llm_extracted_at}
                          ticker={r.ticker}
                          fieldLabel="Non-accrual % (fair value)"
                          testId={`why-non-accrual-${r.ticker}`}
                        />
                      </span>
                    </td>
                    <td className="px-3 py-3 font-mono text-right text-graphite/70">
                      <span className="inline-flex items-center justify-end">
                        {r.pik_income_pct != null ? `${r.pik_income_pct.toFixed(1)}%` : "—"}
                        <WhyTooltip
                          excerpt={r.llm_excerpts?.pik_income_pct}
                          model={r.llm_model}
                          confidence={r.llm_confidence}
                          extractedAt={r.llm_extracted_at}
                          ticker={r.ticker}
                          fieldLabel="PIK income % of total investment income"
                          testId={`why-pik-${r.ticker}`}
                        />
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {r.management_type ? (
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-mono tracking-widest uppercase ${MGMT_CHIP[r.management_type] || "bg-stone text-graphite/60"}`}>
                          {mgmtLabel(r.management_type)}
                        </span>
                      ) : <span className="text-graphite/45 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isAdmin && (
                        <button
                          data-testid={`bdc-edit-${r.ticker}`}
                          onClick={() => setEditTicker(r.ticker)}
                          title="Edit BDC fundamentals (admin)"
                          className="p-1.5 text-graphite/60 hover:text-paars-dark hover:bg-stone/50"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="border border-stone bg-champagne p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-graphite/55 font-bold mb-3">// HOW_TO_READ</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-graphite/70">
            <div className="flex gap-2"><TrendingUp className="w-4 h-4 text-groen-dark flex-shrink-0 mt-0.5" /><span><strong>Coverage ≥ 1.0×</strong> · TTM NII ≥ TTM dividend. The dividend is earned, not borrowed.</span></div>
            <div className="flex gap-2"><TrendingDown className="w-4 h-4 text-burgundy flex-shrink-0 mt-0.5" /><span><strong>Coverage &lt; 1.0×</strong> · Distributions exceed earnings → possible NAV erosion or future cut.</span></div>
            <div className="flex gap-2"><AlertCircle className="w-4 h-4 text-burgundy flex-shrink-0 mt-0.5" /><span><strong>Non-Acc &gt; 2%</strong> · Notable share of the portfolio is non-performing. Watch portfolio rotation.</span></div>
          </div>
        </div>
      </div>

      {editTicker && (
        <BDCEditDialog
          ticker={editTicker}
          onClose={() => setEditTicker(null)}
          onSaved={() => { setEditTicker(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ testId, label, value, sub, accent = "text-graphite" }) {
  return (
    <div data-testid={testId} className="bg-champagne p-5 flex flex-col justify-between min-h-[120px]">
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-graphite/55">{label}</div>
      <div>
        <div className={`text-3xl font-mono tracking-tight ${accent}`}>{value}</div>
        {sub && <div className="text-xs font-mono text-graphite/55 mt-1">{sub}</div>}
      </div>
    </div>
  );
}
